import type { AccessInfo, AccessLevel } from "@queueryte/contracts";

export interface ScopeSelection { siteId: string; queueId: string; }
export type NavigationPage = "overview" | "sites" | "analytics" | "analytics-preview" | "alerts" | "kiosks" | "users" | "platform";

const storageKey = "queueryte.scope";
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const clean = (value: unknown): string => typeof value === "string" && uuid.test(value) ? value : "";

/** A link such as ?site=…&queue=… (used by retired per-queue addresses) wins over the remembered choice. */
export function readSelection(storage: Pick<Storage, "getItem"> | null, search: string): ScopeSelection {
  const params = new URLSearchParams(search);
  if (params.has("site") || params.has("queue")) return { siteId: clean(params.get("site")), queueId: clean(params.get("queue")) };
  try {
    const saved = JSON.parse(storage?.getItem(storageKey) ?? "{}") as Partial<ScopeSelection>;
    return { siteId: clean(saved.siteId), queueId: clean(saved.queueId) };
  } catch { return { siteId: "", queueId: "" }; }
}

export function saveSelection(storage: Pick<Storage, "setItem"> | null, selection: ScopeSelection): void {
  try { storage?.setItem(storageKey, JSON.stringify(selection)); } catch { /* storage can be unavailable */ }
}

/** Keeps the selection inside what the person may see. People limited to chosen sites always have one site selected. */
export function resolveSelection(access: AccessInfo, wanted: ScopeSelection): ScopeSelection {
  const queueSite = wanted.queueId ? access.sites.find((site) => site.queues.some((queue) => queue.id === wanted.queueId)) : undefined;
  const site = queueSite ?? access.sites.find((candidate) => candidate.id === wanted.siteId) ?? (access.allSites ? undefined : access.sites[0]);
  if (site === undefined) return { siteId: "", queueId: "" };
  return { siteId: site.id, queueId: site.queues.some((queue) => queue.id === wanted.queueId) ? wanted.queueId : "" };
}

export function withScope(path: string, selection: ScopeSelection): string {
  const search = new URLSearchParams();
  if (selection.siteId) search.set("siteId", selection.siteId);
  if (selection.queueId) search.set("queueId", selection.queueId);
  const query = search.toString();
  return query ? `${path}${path.includes("?") ? "&" : "?"}${query}` : path;
}

export function navigationFor(level: AccessLevel | null, isPlatformAdmin: boolean): NavigationPage[] {
  const pages: NavigationPage[] = level === null ? [] : level === "guest" ? ["analytics", "analytics-preview"]
    : ["overview", "sites", "analytics", "analytics-preview", "alerts"];
  if (level === "manager" || level === "admin") pages.push("kiosks", "users");
  if (isPlatformAdmin) pages.push("platform");
  return pages;
}

export const levelLabel = (level: AccessLevel): string => ({ admin: "QueueRyte admin", manager: "Client manager", client: "Client", guest: "Guest" })[level];
